import { action, observable, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { LegItemType } from 'src/AppState/ReduxAction';
import { TypeId } from 'src_common/common/websocket2/type';

export type CastState = Map<number, Array<LegItemType>>;

export class ForecastTricastState {
    @observable public forecast: CastState = new Map();
    @observable public tricast: CastState = new Map();

    public constructor(
        private readonly appState: AppState,
        private readonly userId: number
    ) {
        makeAutoObservable(this);
    }

    @action public onSelect = (
        key: string,
        place: number,
        event: EventModel,
        market: MarketModel,
        selection: SelectionModel
    ): void => {
        let state = key === 'tricast' ? this.tricast : this.forecast;
        if (selection.price === undefined) {
            return;
        }
        const leg: LegItemType = {
            eventId: event.id,
            marketId: TypeId.getMarketId(market.id),
            selectionId: selection.id,
            price: selection.price.f,
            isMarketSp: market.sp,
            priceType: 'fp',
        };

        if (place !== 0) {
            this.placeHandle(state, place, leg);
        } else {
            this.anyHandle(state, leg);
        }
        this.finishBet(key, state);
    };

    @action private placeHandle = (state: CastState, place: number, leg: LegItemType): void => {
        if (state.has(place)) {
            state.delete(place);
        } else {
            state.set(place, [leg]);
        }
    };

    @action private anyHandle = (state: CastState, leg: LegItemType): void => {
        const any = state.get(0);
        if (any === undefined) {
            state.set(0, [leg]);
        } else {
            const inAny = any.findIndex((legIn) => legIn.selectionId === leg.selectionId);
            if (inAny > -1) {
                state.set(0, any.splice(inAny, 1));
            } else {
                state.set(0, [...any, leg]);
            }
        }
    };

    private finishBet = (key: string, state: CastState): void => {
        const leg1 = state.get(1);
        const leg2 = state.get(2);
        const leg3 = state.get(3);
        const anyLegs = state.get(0);
        if (key === 'forecast') {
            if (leg1 !== undefined && leg2 !== undefined) {
                this.forecastComplete('FC', [...leg1, ...leg2]);
            } else if (anyLegs !== undefined && anyLegs.length > 1) {
                this.forecastComplete('RFC', anyLegs);
            }
        } else if (key === 'tricast') {
            if (leg1 !== undefined && leg2 !== undefined && leg3 !== undefined) {
                this.tricastComplete('TC', [...leg1, ...leg2, ...leg3]);
            } else if (anyLegs !== undefined && anyLegs.length > 2) {
                this.tricastComplete('CTC', anyLegs);
            }
        }
    };

    @action private tricastComplete = (type: 'TC' | 'CTC', legs: Array<LegItemType>): void => {
        this.tricast = new Map();
        this.appState.reduxAction.onTricastAdd(this.userId, type, legs);
    };

    @action private forecastComplete = (type: 'FC' | 'RFC', legs: Array<LegItemType>): void => {
        this.forecast = new Map();
        this.appState.reduxAction.onForecastAdd(this.userId, type, legs);
    };
}
